/**
 * API client pour les Questionnaires de Formation (Pré / Post / Intermédiaire).
 * Utilisé exclusivement depuis le CRM admin (React).
 */

const _url = (path) =>
  `${process.env.REACT_APP_BACK}/subdomain/${globalThis.localStorage.getItem('tenant')}/api/${path}`;

const _headers = () => {
  const tenant = globalThis.localStorage.getItem('tenant');
  const accessToken = globalThis.localStorage.getItem(`accessToken_${tenant}`);
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
};

const _post = async (path, body) => {
  const resp = await fetch(_url(path), {
    method: 'POST',
    headers: _headers(),
    body: JSON.stringify(body),
  });
  return resp.json();
};

class FormationQuestionnaireApi {
  // =========================================================================
  // Questionnaires
  // =========================================================================

  /**
   * Crée un questionnaire de formation (pre | post | intermediate).
   * @param {number} adminId
   * @param {number} auditId
   * @param {string} name
   * @param {'pre'|'post'|'intermediate'} type
   * @param {1|2|null} tour - requis pour type='intermediate' (Tour 1 ou Tour 2)
   */
  createQuestionnaire(adminId, auditId, name, type, tour = null) {
    return _post('backoffice/formationQuestionnaire/create/', {
      admin_id: adminId,
      audit_id: auditId,
      name,
      type,
      tour,
    });
  }

  /**
   * Retourne tous les questionnaires d'un audit.
   * @param {number} adminId
   * @param {number} auditId
   */
  getAllQuestionnaires(adminId, auditId) {
    return _post('backoffice/formationQuestionnaire/getAll/', {
      admin_id: adminId,
      audit_id: auditId,
    });
  }

  /**
   * Retourne un questionnaire complet avec ses questions et réponses (is_correct inclus).
   * @param {number} adminId
   * @param {number} questionnaireId
   */
  getQuestionnaireById(adminId, questionnaireId) {
    return _post('backoffice/formationQuestionnaire/getById/', {
      admin_id: adminId,
      questionnaire_id: questionnaireId,
    });
  }

  /**
   * Modifie un questionnaire.
   * @param {number} adminId
   * @param {number} questionnaireId
   * @param {object} fields  - {name?, type?, chapter_id?, chapter_title?}
   */
  modifyQuestionnaire(adminId, questionnaireId, fields) {
    return _post('backoffice/formationQuestionnaire/modify/', {
      admin_id: adminId,
      questionnaire_id: questionnaireId,
      ...fields,
    });
  }

  /**
   * Supprime un questionnaire et toutes ses questions/réponses.
   * @param {number} adminId
   * @param {number} questionnaireId
   */
  deleteQuestionnaire(adminId, questionnaireId) {
    return _post('backoffice/formationQuestionnaire/delete/', {
      admin_id: adminId,
      questionnaire_id: questionnaireId,
    });
  }

  /**
   * Retourne les résultats de tous les apprenants pour un questionnaire.
   * @param {number} adminId
   * @param {number} questionnaireId
   */
  getQuestionnaireResults(adminId, questionnaireId) {
    return _post('backoffice/formationQuestionnaire/results/get/', {
      admin_id: adminId,
      questionnaire_id: questionnaireId,
    });
  }

  // =========================================================================
  // Questions
  // =========================================================================

  /**
   * Crée une question dans un questionnaire.
   * @param {number} adminId
   * @param {number} questionnaireId
   * @param {string} question
   * @param {number} questionOrder
   */
  createQuestion(adminId, questionnaireId, question, questionOrder = 0) {
    return _post('backoffice/formationQuestion/create/', {
      admin_id: adminId,
      questionnaire_id: questionnaireId,
      question,
      question_order: questionOrder,
    });
  }

  /**
   * Retourne les questions d'un questionnaire.
   * @param {number} adminId
   * @param {number} questionnaireId
   */
  getQuestionsByQuestionnaire(adminId, questionnaireId) {
    return _post('backoffice/formationQuestion/getByQuestionnaire/', {
      admin_id: adminId,
      questionnaire_id: questionnaireId,
    });
  }

  /**
   * Modifie le texte d'une question.
   * @param {number} adminId
   * @param {number} questionId
   * @param {string} question
   */
  modifyQuestion(adminId, questionId, question) {
    return _post('backoffice/formationQuestion/modify/', {
      admin_id: adminId,
      question_id: questionId,
      question,
    });
  }

  /**
   * Supprime une question et ses réponses.
   * @param {number} adminId
   * @param {number} questionId
   */
  deleteQuestion(adminId, questionId) {
    return _post('backoffice/formationQuestion/delete/', {
      admin_id: adminId,
      question_id: questionId,
    });
  }

  /**
   * Réordonne les questions d'un questionnaire.
   * @param {number} adminId
   * @param {Array<{id: number, question_order: number}>} questions
   */
  modifyQuestionOrder(adminId, questions) {
    return _post('backoffice/formationQuestion/modifyOrder/', {
      admin_id: adminId,
      questions,
    });
  }

  // =========================================================================
  // Réponses
  // =========================================================================

  /**
   * Crée une réponse pour une question.
   * Si isCorrect=true, les autres réponses de la question passent à false.
   * @param {number} adminId
   * @param {number} questionId
   * @param {string} answer
   * @param {number} answerOrder
   * @param {boolean} isCorrect
   */
  createAnswer(adminId, questionId, answer, answerOrder = 0, isCorrect = false) {
    return _post('backoffice/formationQuestionAnswer/create/', {
      admin_id: adminId,
      question_id: questionId,
      answer,
      answer_order: answerOrder,
      is_correct: isCorrect,
    });
  }

  /**
   * Retourne les réponses d'une question.
   * @param {number} adminId
   * @param {number} questionId
   */
  getAnswersByQuestion(adminId, questionId) {
    return _post('backoffice/formationQuestionAnswer/getByQuestion/', {
      admin_id: adminId,
      question_id: questionId,
    });
  }

  /**
   * Modifie une réponse.
   * @param {number} adminId
   * @param {number} answerId
   * @param {object} fields - {answer?, is_correct?}
   */
  modifyAnswer(adminId, answerId, fields) {
    return _post('backoffice/formationQuestionAnswer/modify/', {
      admin_id: adminId,
      answer_id: answerId,
      ...fields,
    });
  }

  /**
   * Supprime une réponse.
   * @param {number} adminId
   * @param {number} answerId
   */
  deleteAnswer(adminId, answerId) {
    return _post('backoffice/formationQuestionAnswer/delete/', {
      admin_id: adminId,
      answer_id: answerId,
    });
  }

  /**
   * Réordonne les réponses d'une question.
   * @param {number} adminId
   * @param {Array<{id: number, answer_order: number}>} answers
   */
  modifyAnswerOrder(adminId, answers) {
    return _post('backoffice/formationQuestionAnswer/modifyOrder/', {
      admin_id: adminId,
      answers,
    });
  }
}

export const formationQuestionnaireApi = new FormationQuestionnaireApi();
